import React from 'react';
import { Task } from '../types';
import { X, CheckSquare, Square, Brain, CheckCircle2 } from 'lucide-react';
import { useTaskStore } from '../store/taskStore';

interface TaskDetailProps {
  task: Task;
  onClose: () => void;
}

export const TaskDetail: React.FC<TaskDetailProps> = ({ task, onClose }) => {
  const toggleStep = useTaskStore(state => state.toggleStep);
  const completeTask = useTaskStore(state => state.completeTask);

  const handleComplete = () => {
    completeTask(task.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="cyberpunk-card w-full max-w-2xl rounded-lg neon-border">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#00fff9] font-mono cyberpunk-title">
              {task.title}
            </h2>
            <div className="flex items-center gap-3">
              {task.status !== 'completed' && (
                <button
                  onClick={handleComplete}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00fff933] text-[#00fff9] rounded-lg font-mono hover:bg-[#00fff966] transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  COMPLETE_MISSION
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1 hover:bg-[#ff00c133] rounded-full text-[#ff00c1] transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="mb-6">
            <p className="text-gray-400">{task.description}</p>
            <div className="mt-4 flex items-center gap-4">
              <span className="px-3 py-1 rounded-full text-sm font-mono"
                style={{
                  backgroundColor:
                    task.priority === 'urgent'
                      ? '#ff00c133'
                      : task.priority === 'normal'
                      ? '#f7d00033'
                      : '#00fff933',
                  color:
                    task.priority === 'urgent'
                      ? '#ff00c1'
                      : task.priority === 'normal'
                      ? '#f7d000'
                      : '#00fff9',
                }}
              >
                {task.priority.toUpperCase()}
              </span>
              <span className="text-sm text-gray-500 font-mono">
                DEADLINE: {new Date(task.dueDate).toLocaleDateString()}
              </span>
              {task.completedAt && (
                <span className="text-sm text-[#00fff9] font-mono">
                  COMPLETED: {new Date(task.completedAt).toLocaleDateString()}
                </span>
              )}
            </div>

            {task.aiReasoning && (
              <div className="mt-4 p-3 bg-[#0d0d14] rounded-lg border border-[#00fff933]">
                <div className="flex items-center gap-2 text-[#00fff9] mb-2">
                  <Brain className="w-4 h-4" />
                  <span className="font-mono text-sm">AI_ANALYSIS</span>
                </div>
                <p className="text-sm text-gray-400">{task.aiReasoning}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <h3 className="font-mono text-[#00fff9]">MISSION_OBJECTIVES</h3>
            {task.steps.map((step) => (
              <div
                key={step.id}
                className="flex items-center gap-3 p-3 hover:bg-[#00fff911] rounded-lg transition-colors cursor-pointer"
                onClick={() => task.status !== 'completed' && toggleStep(task.id, step.id)}
              >
                {step.completed ? (
                  <CheckSquare className="w-5 h-5 text-[#00fff9]" />
                ) : (
                  <Square className="w-5 h-5 text-gray-600" />
                )}
                <span className={step.completed ? 'text-gray-500 line-through' : 'text-gray-300'}>
                  {step.description}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0d0d14] px-6 py-4 rounded-b-lg border-t border-[#00fff922]">
          <div className="flex justify-between items-center">
            <div className="text-sm text-[#00fff9] font-mono">
              COMPLETION: {task.progress}%
            </div>
            <div className="w-64 bg-gray-800 rounded-full h-2">
              <div
                className="rounded-full h-2 transition-all duration-300"
                style={{
                  width: `${task.progress}%`,
                  background: 'linear-gradient(90deg, #00fff9, #ff00c1)'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};